import { useQuery } from '@tanstack/react-query';
import { api, type MType } from '@/lib/api';
import { KILL_GROWTH_CLASS_META } from '@/lib/killGrowth';
import { MTypeIcon } from '@/components/MTypeIcon';
import { Loading, Panel, Th, fmtDate, fmtM, playerLabel } from './shared';

// Plan 054 — troop composition for DS/CS planning.

const TYPES: MType[] = ['planes', 'rockets', 'tanks'];
const TYPE_META: Record<MType, { label: string; bar: string }> = {
  planes: { label: 'Planes', bar: 'bg-sky-500' },
  rockets: { label: 'Rockets', bar: 'bg-rose-500' },
  tanks: { label: 'Tanks', bar: 'bg-emerald-500' },
};

/** One horizontal 100 % bar split by troop type. */
function MixBar({ values }: { values: Record<MType, number> }) {
  const total = TYPES.reduce((sum, t) => sum + values[t], 0);
  if (total === 0) return <div className="h-3 rounded bg-slate-800" />;
  return (
    <div className="flex h-3 overflow-hidden rounded">
      {TYPES.map((t) =>
        values[t] > 0 ? (
          <div
            key={t}
            className={TYPE_META[t].bar}
            style={{ width: `${(values[t] / total) * 100}%` }}
            title={`${TYPE_META[t].label}: ${Math.round((values[t] / total) * 100)} %`}
          />
        ) : null,
      )}
    </div>
  );
}

function share(n: number, total: number) {
  return total === 0 ? '—' : `${Math.round((n / total) * 100)} %`;
}

export default function TroopMixTab() {
  const q = useQuery({ queryKey: ['insights', 'troop-mix'], queryFn: api.getAdminTroopMix });
  if (q.isLoading || !q.data) return <Loading />;
  const data = q.data;
  const byType = Object.fromEntries(data.byType.map((t) => [t.type, t])) as Record<
    MType,
    (typeof data.byType)[number]
  >;
  const m1Count = Object.fromEntries(TYPES.map((t) => [t, byType[t].m1Players])) as Record<MType, number>;
  const m1Power = Object.fromEntries(TYPES.map((t) => [t, byType[t].m1Power])) as Record<MType, number>;
  const squadPower = Object.fromEntries(TYPES.map((t) => [t, byType[t].squadPower])) as Record<MType, number>;
  const totalPlayers = data.playersWithData;

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-400">
        Troop types from each player's latest report ({data.playersWithData} of{' '}
        {data.activePlayers} active players). M1 is the strongest squad.
      </p>

      <Panel title="Alliance mix">
        <div className="space-y-3">
          {[
            { label: 'M1 type (players)', values: m1Count, unit: 'players' },
            { label: 'M1 power', values: m1Power, unit: 'M' },
            { label: 'All squads (M1–M3) power', values: squadPower, unit: 'M' },
          ].map((row) => {
            const total = TYPES.reduce((s, t) => s + row.values[t], 0);
            return (
              <div key={row.label}>
                <div className="mb-1 text-xs text-slate-400">{row.label}</div>
                <MixBar values={row.values} />
                <div className="mt-1 flex flex-wrap gap-x-4 text-xs tabular-nums text-slate-300">
                  {TYPES.map((t) => (
                    <span key={t} className="inline-flex items-center gap-1">
                      <MTypeIcon type={t} size={12} />
                      {row.unit === 'players' ? row.values[t] : fmtM(row.values[t], 0)}
                      <span className="text-slate-500">({share(row.values[t], total)})</span>
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </Panel>

      <div className="grid gap-4 md:grid-cols-2">
        <Panel title="M1 type per power class">
          <div className="space-y-3">
            {(['top', 'mid', 'rising'] as const).map((key) => {
              const c = data.byClass.find((x) => x.key === key)!;
              const meta = KILL_GROWTH_CLASS_META[key];
              return (
                <div key={key}>
                  <div className="mb-1 flex justify-between text-xs">
                    <span style={{ color: meta.color }}>{meta.label}</span>
                    <span className="tabular-nums text-slate-500">{c.players} players</span>
                  </div>
                  <MixBar values={c.m1} />
                  <div className="mt-1 flex gap-x-4 text-xs tabular-nums text-slate-400">
                    {TYPES.map((t) => (
                      <span key={t} className="inline-flex items-center gap-1">
                        <MTypeIcon type={t} size={12} /> {c.m1[t]}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </Panel>

        <Panel title="M1 mix at month end">
          {data.monthly.length === 0 ? (
            <div className="text-sm text-slate-500">Not enough history yet.</div>
          ) : (
            <div className="space-y-2">
              {data.monthly.map((m) => (
                <div key={m.month} className="grid grid-cols-[90px_1fr_auto] items-center gap-2 text-xs">
                  <span className="text-slate-400">{m.label}</span>
                  <MixBar values={m.m1} />
                  <span className="tabular-nums text-slate-500">{m.players}</span>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      <Panel title={`M1 type switches · last 90 days (${data.switches.length})`}>
        {data.switches.length === 0 ? (
          <div className="text-sm text-slate-500">No switches.</div>
        ) : (
          <>
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <Th left>Player</Th>
                  <Th left>Switch</Th>
                  <Th>M1</Th>
                  <Th>Date</Th>
                </tr>
              </thead>
              <tbody>
                {data.switches.map((s) => (
                  <tr key={`${s.player.id}-${s.at}`} className="border-t border-slate-800/60">
                    <td className="px-2 py-1">{playerLabel(s.player)}</td>
                    <td className="px-2 py-1">
                      <span className="inline-flex items-center gap-1">
                        <MTypeIcon type={s.from} showLabel /> → <MTypeIcon type={s.to} showLabel />
                      </span>
                    </td>
                    <td className="px-2 py-1 text-right tabular-nums text-slate-300">
                      {fmtM(s.m1Before, 1)} → {fmtM(s.m1After, 1)}
                    </td>
                    <td className="px-2 py-1 text-right tabular-nums text-slate-400">{fmtDate(s.at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-2 text-[11px] text-slate-500">
              Players with a switch around a month boundary are left out of the strength
              push's "Biggest M1 jump" tile.
            </p>
          </>
        )}
      </Panel>

      <p className="text-[11px] text-slate-500">
        {totalPlayers} players counted. Month-end mix uses each player's latest report
        within 60 days before the month's end.
      </p>
    </div>
  );
}
